import React from "react";
import { FilterSelect, FilterSelectOption } from "./filter-select";
import { Input } from "../ui/input";
import { Plus, Minus } from "lucide-react";
import { FilterCondition, FilterField } from "@/interfaces/filter.interface";
import { FilterOperator } from "@/constants/filter.constant";
import { MultiSelectWithSearch } from "./filter-multi-select";

/**
 * Props for the FilterRow component
 */
interface FilterRowProps {
  /** Filter condition to display */
  condition: FilterCondition;
  /** Available filter fields */
  availableFields: FilterField[];
  /** Fields already used in other conditions */
  usedFields: string[];
  /** Whether this is an add row. Defaults to false. */
  isAddRow?: boolean;
  /** Index of the row. Defaults to 0. */
  index?: number;
  /** Callback fired when field changes */
  onFieldChange: (fieldId: string) => void;
  /** Callback fired when operator changes */
  onOperatorChange: (operator: FilterOperator) => void;
  /** Callback fired when value changes */
  onValueChange: (valueOrArray: string | string[]) => void;
  /** Callback fired when row is removed */
  onRemove?: () => void;
  /** Callback fired when add button is clicked */
  onAdd?: () => void;
  /** Whether another filter can be added */
  canAdd: boolean;
  /** Whether maximum filters reached */
  maxReached: boolean;
}

/**
 * FilterRow component displays a single filter condition row with field, operator,
 * and value inputs. Supports different input types (text, number, date, select, multiselect)
 * based on the selected field. Shows add/remove buttons based on position and limits.
 * 
 * @component
 * @param {FilterRowProps} props - Component props
 * @param {FilterCondition} props.condition - Filter condition
 * @param {FilterField[]} props.availableFields - Available fields
 * @param {string[]} props.usedFields - Used field IDs
 * @param {boolean} [props.isAddRow=false] - Whether this is an add row
 * @param {number} [props.index=0] - Row index
 * @param {(fieldId: string) => void} props.onFieldChange - Field change callback
 * @param {(operator: FilterOperator) => void} props.onOperatorChange - Operator change callback
 * @param {(valueOrArray: string | string[]) => void} props.onValueChange - Value change callback
 * @param {() => void} [props.onRemove] - Remove callback
 * @param {() => void} [props.onAdd] - Add callback
 * @param {boolean} props.canAdd - Whether can add more
 * @param {boolean} props.maxReached - Whether max reached
 * @returns {JSX.Element} Filter row with field, operator, value inputs and actions
 * 
 * @example
 * ```tsx
 * <FilterRow
 *   condition={condition}
 *   availableFields={fields}
 *   usedFields={used}
 *   onFieldChange={handleFieldChange}
 *   onOperatorChange={handleOperatorChange}
 *   onValueChange={handleValueChange}
 * />
 * ```
 */
export const FilterRow: React.FC<FilterRowProps> = ({
  condition,
  availableFields,
  usedFields,
  isAddRow = false,
  index = 0,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  onAdd,
  canAdd,
  maxReached,
}) => {
  const selectedField = availableFields.find((f) => f.id === condition.field);

  const getAvailableFields = (): FilterField[] => {
    if (isAddRow) {
      return availableFields.filter((f) => !usedFields.includes(f.id));
    }
    return availableFields;
  };

  const fieldsOptions: FilterSelectOption[] = getAvailableFields().map(
    (field) => ({
      value: field.id,
      label: field.label,
    })
  );

  const operatorOptions: FilterSelectOption[] = selectedField?.operators
    ? Object.values(selectedField.operators).map((op) => ({
        value: op,
        label: op.replace(/_/g, " "),
      }))
    : [];

  const renderValueInput = () => {
    if (!selectedField) {
      return (
        <Input
          placeholder="Value"
          value={condition.value ?? ""}
          onChange={(e) => onValueChange(e.target.value)}
          disabled
          className="w-full"
        />
      );
    }

    // select / multiselect handling
    if ((selectedField.type === "select" || selectedField.type === "multiselect") && selectedField.options) {
      const valueOptions: FilterSelectOption[] = selectedField.options.map((opt) => ({
        value: opt,
        label: opt,
      }));

      if (selectedField.type === "multiselect") {
        return (
          <MultiSelectWithSearch
            options={valueOptions}
            value={condition.values ?? []}
            onChange={(values) => onValueChange(values)}
            placeholder="Select values"
            className="w-full"
            />
        );
      }

      // single select
      return (
        <FilterSelect
          options={valueOptions}
          value={condition.value ?? ""}
          onChange={(val) => onValueChange(val)}
          placeholder={selectedField.placeholder}
          className="w-full"
        />
      );
    }

    // default input types: number, date, text
    return (
      <Input
        type={selectedField.type === "number" ? "number" : selectedField.type === "date" ? "date" : "text"}
        placeholder={selectedField.placeholder || "Value"}
        value={condition.value ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
        className="w-full"
      />
    );
  };

  const isValid = !!selectedField && !!condition.operator && (condition.value ?? "").trim() !== "";

  if (isAddRow) {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="w-9 h-9 flex-shrink-0" />

        <div className="flex-1 min-w-0 max-w-64">
          <FilterSelect
            options={fieldsOptions}
            value={condition.field}
            onChange={(value) => onFieldChange(value)}
            placeholder="Select a field"
            className="w-full"
          />
        </div>

        <div className="flex-1 min-w-0  max-w-64">
          <FilterSelect
            options={operatorOptions}
            value={condition.operator}
            onChange={(value) => onOperatorChange(value as FilterOperator)}
            className="w-full"
            placeholder="Select an operator"
          />
        </div>

        <div className="flex-1 min-w-0">
          {renderValueInput()}
        </div>

        <button
          type="button"
          onClick={onAdd}
          disabled={!canAdd || maxReached || !isValid}
          className="flex-shrink-0 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-primary-500 hover:bg-primary-600 text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-400"
          aria-label="Add filter"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const isFirstRow = index === 0;

  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        {onRemove ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isFirstRow) {
                onRemove();
              }
            }}
            disabled={isFirstRow}
            className="h-6 w-6 bg-primary-500 hover:bg-primary-600 text-white rounded-full p-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-400"
            aria-label="Remove filter"
          >
            <Minus className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div className="flex-1 min-w-0 max-w-64">
        <FilterSelect
          options={fieldsOptions}
          value={condition.field || undefined}
          onChange={(value) => onFieldChange(value)}
          placeholder="Select a field"
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0 max-w-64">
        <FilterSelect
          options={operatorOptions}
          value={condition.operator && condition.operator !== FilterOperator.NONE ? condition.operator : undefined}
          onChange={(value) => onOperatorChange(value as FilterOperator)}
          placeholder="Select an operator"
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        {renderValueInput()}
      </div>

      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        {canAdd && onAdd && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAdd();
            }}
            className="h-6 w-6 bg-primary-500 hover:bg-primary-600 text-white rounded-full p-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            aria-label="Add filter"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

